import { useEffect, useRef, useState } from 'react';
import { ITEM_COLS, ITEM_COLS_DRAFT, ConceptTableHead, ConceptRow, TotalValue, DocKV } from './EstimateDetail.js';
import { useDebouncedFlush } from '../../hooks/useDebouncedFlush.js';
import {
  ConceptEditor, ConceptPreviewToggle, useConceptPreview,
  EMPTY_LINE, linesFromItems, itemsFromLines, previewItems,
  type LineItemForm,
} from './ConceptEditor.js';
import { useTranslation } from 'react-i18next';
import { LoadingState } from '../ui/LoadingState.js';
import { Card, CardTitle } from '../ui/Card.js';
import { Input } from '../ui/Input.js';
import { Dropdown } from '../ui/Dropdown.js';
import { Textarea } from '../ui/Textarea.js';
import { Button } from '../ui/Button.js';
import { formatMoney } from '../../lib/money.js';
import { previewTotals } from '../../lib/billingTotals.js';
import { useInvoice, useUpdateInvoice, type EstimateLineItem } from '../../hooks/useBilling.js';
import { InvoicePaymentCard } from './InvoicePaymentCard.js';
import { InvoiceDraftCardConnected } from './InvoiceDraftCardConnected.js';
import { InvoiceReplyCardConnected } from './InvoiceReplyCardConnected.js';
import { useInvoiceDrafts, useInvoiceReplies } from '../../hooks/useInvoiceOutreach.js';


function formatDocDate(value: number): string {
  return new Date(value).toLocaleDateString('es', { year: 'numeric', month: 'short', day: 'numeric' });
}

export interface InvoiceDetailProps {
  id: number;
  /** Nombre del cliente para la tarjeta Documento (el caller lo resuelve). */
  clientName?: string | undefined;
}

// Editable while 'draft' (line items, tax rate, notes, due date) — mirrors
// EstimateDetail's edit UX. accountId/contactId/currency are frozen at
// conversion and never shown as editable here.
// Mismo layout de tarjetas del diseño que EstimateDetail: duo
// [Documento | Totales] + Conceptos + Notas. Sin pestañas.
export function InvoiceDetail({ id, clientName }: InvoiceDetailProps) {
  const { t } = useTranslation();
  const { data: invoice, isLoading } = useInvoice(id);
  const { data: draftsData } = useInvoiceDrafts(id);
  const { data: repliesData } = useInvoiceReplies(id);
  const drafts = draftsData?.drafts ?? [];
  const replies = repliesData?.replies ?? [];
  const updateInvoice = useUpdateInvoice();

  const [lines, setLines] = useState<LineItemForm[]>([{ ...EMPTY_LINE }]);
  const [taxPercent, setTaxPercent] = useState('0');
  const [discount, setDiscount] = useState('');
  const [discountLabel, setDiscountLabel] = useState('');
  const [deposit, setDeposit] = useState('');
  const [notes, setNotes] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [saved, setSaved] = useState(false);
  // Lo último que se mandó, serializado. null = todavía no se cargó la factura.
  const ultimoGuardado = useRef<string | null>(null);
  const { preview: previewConcepts, toggle: togglePreviewConcepts } = useConceptPreview();

  useEffect(() => {
    if (!invoice) return;
    ultimoGuardado.current = null;
    setLines(linesFromItems(JSON.parse(invoice.lineItemsJson) as EstimateLineItem[]));
    setTaxPercent((invoice.taxRate / 100).toString());
    setDiscount(invoice.discountAmount ? (invoice.discountAmount / 100).toFixed(2) : '');
    setDiscountLabel(invoice.discountLabel ?? '');
    setDeposit(invoice.depositPercent != null ? String(invoice.depositPercent) : '');
    setNotes(invoice.notes ?? '');
    setDueDate(invoice.dueDate ?? '');
  }, [invoice]);

  // Autoguardado, igual que en la cotización: una factura en borrador se guarda
  // sola a los 900ms de dejar de escribir, y también al salir de la pantalla.
  // La comparación contra lo último mandado evita el guardado fantasma de la
  // carga inicial.
  //
  // Va ANTES del early return de carga: un hook después de un `return` cambia
  // de orden entre renders y React lo prohíbe.
  const autoguardado = useDebouncedFlush(() => { handleSave(); }, 900, true);

  useEffect(() => {
    if (!invoice || invoice.status !== 'draft' || updateInvoice.isPending) return;
    const serializado = JSON.stringify(armarPayload());
    if (ultimoGuardado.current === null) { ultimoGuardado.current = serializado; return; }
    if (ultimoGuardado.current === serializado) return;
    autoguardado.queue();
  // `autoguardado` es estable entre renders.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice, updateInvoice.isPending, lines, taxPercent, discount, discountLabel, deposit, notes, dueDate]);

  if (isLoading || !invoice) return <LoadingState size="sm" />;

  const isDraft = invoice.status === 'draft';
  const currency = invoice.currency;
  const preview = previewTotals(lines, taxPercent, discount, deposit);
  const savedItems = JSON.parse(invoice.lineItemsJson) as EstimateLineItem[];
  const taxLabel = `${t('billing.estimates.tax')} (${(Number(taxPercent) || 0)}%)`;
  const conceptCols = isDraft ? ITEM_COLS_DRAFT : ITEM_COLS;

  /** Lo editable de la factura, tal como se manda. */
  function armarPayload() {
    return {
      lineItems: itemsFromLines(lines),
      taxRate: Math.max(0, Math.round((Number(taxPercent) || 0) * 100)),
      discountAmount: Math.max(0, Math.round((Number(discount) || 0) * 100)),
      discountLabel: discountLabel.trim() || null,
      depositPercent: Math.min(100, Math.max(0, Math.trunc(Number(deposit) || 0))) || null,
      notes: notes.trim() || null,
      dueDate: dueDate || null,
    };
  }

  function handleSave() {
    const payload = armarPayload();
    ultimoGuardado.current = JSON.stringify(payload);
    updateInvoice.mutate({ id, ...payload }, {
      onSuccess: () => { setSaved(true); setTimeout(() => setSaved(false), 2000); },
    });
  }


  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
      {/* Duo del diseño: Documento | Totales */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--evo-space-4)', alignItems: 'stretch' }}>
        <Card header={<CardTitle>{t('billing.doc.document')}</CardTitle>}>
          <DocKV label={t('billing.doc.folio')}>{invoice.number ?? `#${invoice.id}`}</DocKV>
          <DocKV label={t('billing.doc.client')}>{clientName}</DocKV>
          <DocKV label={t('billing.doc.issued')}>{formatDocDate(invoice.createdAt)}</DocKV>
          <DocKV label={t('billing.facturacion.dueDate')}>
            {isDraft ? (
              <Input type="date" value={dueDate} onChange={setDueDate} size="sm" />
            ) : (
              dueDate || undefined
            )}
          </DocKV>
          {/* Idioma del documento: a un cliente de Florida se le
              factura en inglés y a uno de Santo Domingo en español,
              desde la misma cuenta. Vacío = el de la cuenta. */}
          <DocKV label={t('billing.doc.language')} last>
            <Dropdown<string>
              value={invoice.language ?? 'account'}
              options={[
                { value: 'account', label: t('billing.doc.languageAccount') },
                { value: 'es', label: t('settings.generalSection.langEs') },
                { value: 'en', label: t('settings.generalSection.langEn') },
              ]}
              onChange={v => updateInvoice.mutate({ id, language: v === 'account' ? null : v })}
              size="sm"
            />
          </DocKV>
        </Card>

        <Card header={<CardTitle>{t('billing.doc.totals')}</CardTitle>}>
          <DocKV label={t('billing.estimates.subtotal')}>{formatMoney(preview.subtotal, currency)}</DocKV>
          {isDraft ? (
            <DocKV label={t('billing.doc.discount')}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
                <span style={{ width: 90 }}>
                  <Input type="number" value={discount} onChange={v => { setDiscount(v); setSaved(false); }} size="sm" fullWidth />
                </span>
                <span style={{ width: 130 }}>
                  <Input value={discountLabel} onChange={v => { setDiscountLabel(v); setSaved(false); }} placeholder={t('billing.doc.discountReason')} size="sm" fullWidth />
                </span>
              </span>
            </DocKV>
          ) : preview.discountAmount > 0 ? (
            <DocKV label={discountLabel ? `${t('billing.doc.discount')} · ${discountLabel}` : t('billing.doc.discount')}>
              {`−${formatMoney(preview.discountAmount, currency)}`}
            </DocKV>
          ) : null}
          <DocKV label={isDraft ? t('billing.estimates.taxRatePercent') : taxLabel}>
            {isDraft ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
                <span style={{ width: 70 }}>
                  <Input type="number" value={taxPercent} onChange={v => { setTaxPercent(v); setSaved(false); }} size="sm" fullWidth />
                </span>
                {formatMoney(preview.taxAmount, currency)}
              </span>
            ) : (
              formatMoney(preview.taxAmount, currency)
            )}
          </DocKV>
          <DocKV label={t('billing.estimates.total')}>
            <TotalValue amount={preview.total} currency={currency} />
          </DocKV>
          {/* Anticipo: cuánto tiene que entrar para arrancar. Va debajo del
              total porque se calcula sobre él. */}
          {isDraft ? (
            <DocKV label={t('billing.doc.deposit')} last>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
                <span style={{ width: 70 }}>
                  <Input type="number" value={deposit} onChange={v => { setDeposit(v); setSaved(false); }} size="sm" fullWidth />
                </span>
                {preview.depositAmount != null ? formatMoney(preview.depositAmount, currency) : '%'}
              </span>
            </DocKV>
          ) : preview.depositAmount != null ? (
            <DocKV label={`${t('billing.doc.deposit')} · ${preview.depositPercent}%`} last>
              {formatMoney(preview.depositAmount, currency)}
            </DocKV>
          ) : null}
        </Card>
      </div>

      {/* Cobro: en qué está, y —si es recurrente— cuándo es el próximo y si la
          tarjeta sigue viva. Se rinde sola a null en una cuenta que factura a
          mano. */}
      <InvoicePaymentCard invoice={invoice} />

      {/* Los correos de ESTA factura, donde se los busca: en la factura. El
          botón de enviar crea un borrador y hasta ahora el borrador vivía
          solo en el historial del contacto — se creaba algo que nadie
          encontraba. Acá se revisa, se edita y se manda. */}
      {(drafts.length > 0 || replies.length > 0) && (
        <Card header={<CardTitle>{t('billing.doc.emails')}</CardTitle>} padding="md">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
            {drafts.map(d => (
              <InvoiceDraftCardConnected key={d.id} draft={d} invoiceId={invoice.id} />
            ))}
            {replies.map(r => (
              <InvoiceReplyCardConnected key={r.id} reply={r} invoiceId={invoice.id} />
            ))}
          </div>
        </Card>
      )}

      <Card
        header={
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
            <div style={{ flex: 1, minWidth: 0 }}><CardTitle>{t('billing.doc.concepts')}</CardTitle></div>
            {isDraft && <ConceptPreviewToggle preview={previewConcepts} onToggle={togglePreviewConcepts} />}
          </div>
        }
      >
        <ConceptTableHead cols={conceptCols} />
        {isDraft && !previewConcepts ? (
          <ConceptEditor lines={lines} onChange={ls => { setLines(ls); setSaved(false); }} currency={currency} cols={conceptCols} />
        ) : (
          (isDraft ? previewItems(lines) : savedItems).map((item, idx, arr) => (
            <ConceptRow key={idx} item={item} currency={currency} last={idx === arr.length - 1} />
          ))
        )}
      </Card>

      <Card header={<CardTitle>{t('billing.estimates.notes')}</CardTitle>}>
        {isDraft ? (
          <Textarea value={notes} onChange={v => { setNotes(v); setSaved(false); }} rows={3} fullWidth />
        ) : (
          <p style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', whiteSpace: 'pre-wrap', margin: 0, lineHeight: 1.6 }}>
            {notes || '—'}
          </p>
        )}
      </Card>

      {isDraft && (
        <div>
          <Button
            tone="cyan"
            variant={saved ? 'outline' : 'solid'}
            size="sm"
            loading={updateInvoice.isPending}
            onClick={handleSave}
          >
            {updateInvoice.isPending ? t('billing.estimates.saving') : saved ? t('billing.estimates.saved') : t('billing.estimates.save')}
          </Button>
        </div>
      )}
    </div>
  );
}
